import React, { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../../components/Layout/Layout";
import {
  Card,
  Button,
  Badge,
  SearchInput,
  Select,
  Spinner,
  EmptyState,
  Modal,
  Tooltip,
  ConfirmDialog,
} from "../../components/UI";
import { followUpsAPI, usersAPI } from "../../services/api";
import FollowUpsCalendar from "../../components/FollowUps/FollowUpsCalendar";
import toast from "react-hot-toast";
import {
  HiOutlineCalendar,
  HiOutlineClock,
  HiOutlinePhone,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineChevronUp,
  HiOutlineChevronDown,
  HiOutlineFilter,
  HiOutlineRefresh,
} from "react-icons/hi";
import { format, isToday, isTomorrow, isPast, parseISO } from "date-fns";

const statusOptions = [
  { value: "", label: "All Status" },
  { value: "pending", label: "Pending" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

const typeOptions = [
  { value: "", label: "All Types" },
  { value: "call", label: "Call" },
  { value: "email", label: "Email" },
  { value: "visit", label: "Visit" },
  { value: "meeting", label: "Meeting" },
  { value: "other", label: "Other" },
];

const dateRangeOptions = [
  { value: "", label: "All Dates" },
  { value: "today", label: "Today" },
  { value: "tomorrow", label: "Tomorrow" },
  { value: "thisWeek", label: "This Week" },
  { value: "overdue", label: "Overdue" },
];

// Build compact pagination
const buildPageList = (current, total) => {
  const pages = [];
  const set = new Set([1, total, current, current - 1, current + 1]);
  for (const p of set) {
    if (p >= 1 && p <= total) pages.push(p);
  }
  pages.sort((a, b) => a - b);
  const result = [];
  for (let i = 0; i < pages.length; i++) {
    result.push(pages[i]);
    if (i < pages.length - 1 && pages[i + 1] - pages[i] > 1) {
      result.push("...");
    }
  }
  return result;
};

const FollowUpsList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  // Read initial filters from URL search params (for deep-linking from dashboard)
  const initialStatus = searchParams.get("status") || "";
  const initialDateRange = searchParams.get("dateRange") || "";

  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [typeFilter, setTypeFilter] = useState("");
  const [assignedFilter, setAssignedFilter] = useState("");
  const [dateRange, setDateRange] = useState(initialDateRange);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState("scheduledDate");
  const [sortOrder, setSortOrder] = useState("desc");

  // Hover tooltip for row notes
  const [hoverTooltip, setHoverTooltip] = useState({ notes: '', top: 0, left: 0, visible: false });

  const handleRowMouseEnter = useCallback((e, notes) => {
    if (!notes) return;
    const row = e.currentTarget;
    const rect = row.getBoundingClientRect();
    setHoverTooltip({
      notes,
      top: rect.top - 8,
      left: rect.left + rect.width / 2,
      visible: true,
    });
  }, []);

  const handleRowMouseLeave = useCallback(() => {
    setHoverTooltip(prev => ({ ...prev, visible: false }));
  }, []);

  // Mark complete modal
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [selectedFollowUp, setSelectedFollowUp] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [followUpToCancel, setFollowUpToCancel] = useState(null);

  // View mode — list (table) or calendar (month view of follow-ups & activities)
  const [view, setView] = useState("list");

  // Compute date params from range selection
  const getDateParams = () => {
    const now = new Date();
    switch (dateRange) {
      case "today": {
        const today = format(now, "yyyy-MM-dd");
        return { dateFrom: today, dateTo: today };
      }
      case "tomorrow": {
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const t = format(tomorrow, "yyyy-MM-dd");
        return { dateFrom: t, dateTo: t };
      }
      case "thisWeek": {
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        return {
          dateFrom: format(startOfWeek, "yyyy-MM-dd"),
          dateTo: format(endOfWeek, "yyyy-MM-dd"),
        };
      }
      case "overdue": {
        return { dateTo: format(now, "yyyy-MM-dd") };
      }
      default:
        return {};
    }
  };

  // Fetch follow-ups
  const { data: followUpsData, isLoading } = useQuery({
    queryKey: [
      "followUps",
      page,
      pageSize,
      statusFilter,
      typeFilter,
      assignedFilter,
      dateRange,
      search,
      sortBy,
      sortOrder,
    ],
    queryFn: async () => {
      const dateParams = getDateParams();
      const params = {
        page,
        limit: pageSize,
        ...(statusFilter && { status: statusFilter }),
        ...(typeFilter && { type: typeFilter }),
        ...(assignedFilter && { assignedTo: assignedFilter }),
        ...dateParams,
        ...(search && { search }),
        sortBy,
        sortOrder,
      };
      const response = await followUpsAPI.getAll(params);
      return response.data || {};
    },
  });

  // Fetch users for filter
  const { data: usersData } = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const response = await usersAPI.getAll({ role: "telecaller" });
      return response.data?.data || [];
    },
  });

  // Update follow-up mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => followUpsAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["followUps"]);
      toast.success("Follow-up updated");
      setShowCompleteModal(false);
      setSelectedFollowUp(null);
    },
    onError: () => toast.error("Failed to update follow-up"),
  });

  const followUps = followUpsData?.data || [];
  const pagination = followUpsData?.pagination || {};

  const handleSort = (field) => {
    if (sortBy === field) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else {
        setSortBy("scheduledDate");
        setSortOrder("desc");
      }
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setPage(1);
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) {
      return (
        <span className="inline-flex flex-col ml-0.5 opacity-30">
          <HiOutlineChevronUp className="w-3 h-3 -mb-1" />
          <HiOutlineChevronDown className="w-3 h-3" />
        </span>
      );
    }
    return sortOrder === "asc" ? (
      <HiOutlineChevronUp className="w-3.5 h-3.5 ml-0.5 text-primary-600" />
    ) : (
      <HiOutlineChevronDown className="w-3.5 h-3.5 ml-0.5 text-primary-600" />
    );
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return (
          <Badge variant="warning" size="sm">
            Pending
          </Badge>
        );
      case "completed":
        return (
          <Badge variant="success" size="sm">
            Completed
          </Badge>
        );
      case "cancelled":
        return (
          <Badge variant="danger" size="sm">
            Cancelled
          </Badge>
        );
      default:
        return <Badge size="sm">{status}</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = parseISO(dateStr);
    if (isToday(date)) return `Today`;
    if (isTomorrow(date)) return `Tomorrow`;
    return format(date, "MMM d, yyyy");
  };

  const isOverdue = (followUp) => {
    if (followUp.status !== "pending") return false;
    const date = parseISO(followUp.scheduledDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const handleMarkComplete = (followUp) => {
    setSelectedFollowUp(followUp);
    setShowCompleteModal(true);
  };

  const confirmMarkComplete = () => {
    if (selectedFollowUp) {
      updateMutation.mutate({
        id: selectedFollowUp._id,
        data: { status: "completed", completedAt: new Date().toISOString() },
      });
    }
  };

  const handleMarkCancelled = (followUp) => {
    setFollowUpToCancel(followUp);
    setShowCancelConfirm(true);
  };

  const confirmCancelFollowUp = () => {
    if (followUpToCancel) {
      updateMutation.mutate({
        id: followUpToCancel._id,
        data: { status: "cancelled" },
      });
    }
    setShowCancelConfirm(false);
    setFollowUpToCancel(null);
  };

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
    setTypeFilter("");
    setAssignedFilter("");
    setDateRange("");
    setPage(1);
  };

  const hasActiveFilters =
    statusFilter || typeFilter || assignedFilter || dateRange || search;

  return (
    <Layout>
      <div className="p-4 lg:p-6 space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
              Follow Ups
            </h1>
            <p className="text-gray-500 text-xs sm:text-sm mt-0.5 sm:mt-1">
              Track and manage scheduled follow-ups
            </p>
          </div>
          <div className="flex gap-1.5 sm:gap-2 flex-wrap items-center">
            {/* List / Calendar view toggle */}
            <div className="flex rounded-lg border-2 border-gray-200 overflow-hidden self-start">
              <button
                type="button"
                onClick={() => setView("list")}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  view === "list"
                    ? "bg-primary-600 text-white"
                    : "bg-white text-gray-600 hover:bg-gray-50"
                }`}
              >
                List
              </button>
              <button
                type="button"
                onClick={() => setView("calendar")}
                className={`px-3 py-1.5 text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${
                  view === "calendar"
                    ? "bg-primary-600 text-white"
                    : "bg-white text-gray-600 hover:bg-gray-50"
                }`}
              >
                <HiOutlineCalendar className="w-4 h-4" />
                Calendar
              </button>
            </div>
            <Tooltip text="Refresh follow-ups data">
              <Button
                variant="outline"
                size="sm"
                icon={<HiOutlineRefresh className="w-4 h-4" />}
                onClick={() =>
                  queryClient.invalidateQueries(["followUps"])
                }
              >
                <span className="hidden sm:inline">Refresh</span>
              </Button>
            </Tooltip>
          </div>
        </div>

        {/* List view (hidden when the calendar view is selected) */}
        {view === "list" ? (
        <>
        {/* Filters */}
        <Card padding="p-3 sm:p-4">
          <div className="flex flex-col justify-between sm:flex-row gap-2 sm:gap-3">
            <div className="flex-1 max-w-[300px]">
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Search by lead name or phone..."
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <Select
                options={statusOptions}
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                className="flex-1 min-w-[130px]"
              />
              <Select
                options={typeOptions}
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setPage(1);
                }}
                className="flex-1 min-w-[120px]"
              />
              <Select
                options={[
                  { value: "", label: "All Assignees" },
                  ...(usersData || []).map((u) => ({
                    value: u._id,
                    label: `${u.firstName} ${u.lastName}`,
                  })),
                ]}
                value={assignedFilter}
                onChange={(e) => {
                  setAssignedFilter(e.target.value);
                  setPage(1);
                }}
                className="flex-1 min-w-[140px]"
              />
              <Select
                options={dateRangeOptions}
                value={dateRange}
                onChange={(e) => {
                  setDateRange(e.target.value);
                  setPage(1);
                }}
                className="flex-1 min-w-[130px]"
              />
              <Select
                options={[
                  { value: 25, label: "25" },
                  { value: 50, label: "50" },
                  { value: 100, label: "100" },
                ]}
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="flex-1 min-w-[70px]"
              />
              {hasActiveFilters && (
                <Tooltip text="Clear all filters">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="text-gray-500"
                  >
                    Clear
                  </Button>
                </Tooltip>
              )}
            </div>
          </div>
        </Card>

        {/* Summary Cards */}
        {!isLoading && followUpsData && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card padding="p-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-yellow-100 flex items-center justify-center">
                  <HiOutlineClock className="w-4 h-4 text-yellow-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Pending</p>
                  <p className="text-lg font-bold text-gray-900">
                    {followUps.filter((f) => f.status === "pending").length}
                  </p>
                </div>
              </div>
            </Card>
            <Card padding="p-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                  <HiOutlineCheckCircle className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Completed</p>
                  <p className="text-lg font-bold text-gray-900">
                    {followUps.filter((f) => f.status === "completed").length}
                  </p>
                </div>
              </div>
            </Card>
            <Card padding="p-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                  <HiOutlineXCircle className="w-4 h-4 text-red-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Cancelled</p>
                  <p className="text-lg font-bold text-gray-900">
                    {followUps.filter((f) => f.status === "cancelled").length}
                  </p>
                </div>
              </div>
            </Card>
            <Card padding="p-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                  <HiOutlineFilter className="w-4 h-4 text-orange-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Total</p>
                  <p className="text-lg font-bold text-gray-900">
                    {pagination.total || 0}
                  </p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Follow-ups List */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : followUps.length === 0 ? (
          <Card>
            <EmptyState
              icon={<HiOutlineCalendar className="w-8 h-8 text-gray-400" />}
              title="No follow-ups found"
              description="Adjust your filters or wait for follow-ups to be scheduled"
            />
          </Card>
        ) : (
          <Card padding="p-0">
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full table-fixed">
                <thead className="bg-gradient-to-r from-secondary-50 to-secondary-100/50 border-b-2 border-secondary-200/50">
                  <tr>
                    <th
                      className="px-4 py-3 lg:px-5 lg:py-3.5 text-left text-xs font-bold text-secondary-600 uppercase tracking-wider cursor-pointer select-none hover:text-primary-700 transition-colors"
                      style={{ width: '10%' }}
                      onClick={() => handleSort("scheduledDate")}
                    >
                      <span className="inline-flex items-center">Date <SortIcon field="scheduledDate" /></span>
                    </th>
                    <th className="px-4 py-3 lg:px-5 lg:py-3.5 text-left text-xs font-bold text-secondary-600 uppercase tracking-wider" style={{ width: '8%' }}>Time</th>
                    <th className="px-4 py-3 lg:px-5 lg:py-3.5 text-left text-xs font-bold text-secondary-600 uppercase tracking-wider" style={{ width: '20%' }}>Lead</th>
                    <th className="px-4 py-3 lg:px-5 lg:py-3.5 text-left text-xs font-bold text-secondary-600 uppercase tracking-wider" style={{ width: '10%' }}>Phone</th>
                    <th
                      className="px-4 py-3 lg:px-5 lg:py-3.5 text-left text-xs font-bold text-secondary-600 uppercase tracking-wider cursor-pointer select-none hover:text-primary-700 transition-colors"
                      style={{ width: '17%' }}
                      onClick={() => handleSort("assignedTo")}
                    >
                      <span className="inline-flex items-center">Assigned To <SortIcon field="assignedTo" /></span>
                    </th>
                    <th
                      className="px-4 py-3 lg:px-5 lg:py-3.5 text-left text-xs font-bold text-secondary-600 uppercase tracking-wider cursor-pointer select-none hover:text-primary-700 transition-colors"
                      style={{ width: '13%' }}
                      onClick={() => handleSort("status")}
                    >
                      <span className="inline-flex items-center">Status <SortIcon field="status" /></span>
                    </th>
                    <th className="px-4 py-3 lg:px-5 lg:py-3.5 text-left text-xs font-bold text-secondary-600 uppercase tracking-wider" style={{ width: '8%' }}>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-100">
                  {followUps.map((fu) => {
                    const overdue = isOverdue(fu);
                    const leadName = fu.leadId?.name || "Unknown";
                    const leadPhone = fu.leadId?.phone || "-";
                    const assigneeName = fu.assignedTo
                      ? `${fu.assignedTo.firstName} ${fu.assignedTo.lastName}`
                      : "Unassigned";

                    return (
                      <tr
                        key={fu._id}
                        className={`transition-all duration-150 hover:bg-gradient-to-r hover:from-primary-50/30 hover:to-transparent cursor-pointer ${
                          overdue ? "bg-red-50/40" : ""
                        }`}
                        onMouseEnter={(e) => handleRowMouseEnter(e, fu.notes)}
                        onMouseLeave={handleRowMouseLeave}
                        onClick={() => {
                          if (fu.leadId?._id) {
                            navigate(`/app/leads/${fu.leadId._id}`);
                          }
                        }}
                      >
                        <td className="px-4 py-3 lg:px-5 lg:py-3.5 overflow-hidden text-ellipsis whitespace-nowrap text-left" onClick={(e) => e.stopPropagation()}>
                          <span
                            className={`text-sm font-medium ${
                              overdue ? "text-red-600" : "text-gray-900"
                            }`}
                          >
                            {formatDate(fu.scheduledDate)}
                          </span>
                        </td>
                        <td className="px-4 py-3 lg:px-5 lg:py-3.5 overflow-hidden text-ellipsis whitespace-nowrap text-left">
                          <span className="text-sm text-gray-600">
                            {fu.scheduledTime || "-"}
                          </span>
                        </td>
                        <td className="px-4 py-3 lg:px-5 lg:py-3.5">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (fu.leadId?._id) {
                                navigate(`/app/leads/${fu.leadId._id}`);
                              }
                            }}
                            className="text-sm font-medium text-primary-600 hover:text-primary-800 hover:underline truncate block max-w-full"
                            disabled={!fu.leadId?._id}
                          >
                            {leadName}
                          </button>
                        </td>
                        <td className="px-4 py-3 lg:px-5 lg:py-3.5 overflow-hidden text-ellipsis whitespace-nowrap text-left">
                          <span className="text-sm text-gray-600">
                            {leadPhone}
                          </span>
                        </td>
                        <td className="px-4 py-3 lg:px-5 lg:py-3.5 text-left">
                          <Tooltip text={assigneeName}>
                            <span className="text-sm text-gray-700 block truncate max-w-full">
                              {assigneeName}
                            </span>
                          </Tooltip>
                        </td>
                        <td className="px-4 py-3 lg:px-5 lg:py-3.5 text-left">
                          <div className="flex items-center gap-1.5 overflow-hidden text-ellipsis whitespace-nowrap">
                            {getStatusBadge(fu.status)}
                            {overdue && (
                              <span className="text-xs font-medium text-red-500 whitespace-nowrap">
                                Overdue
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 lg:px-5 lg:py-3.5 text-left" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            {fu.status === "pending" && (
                              <>
                                <Tooltip text="Mark Complete">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    icon={<HiOutlineCheckCircle className="w-4 h-4 text-green-600" />}
                                    onClick={() => handleMarkComplete(fu)}
                                  />
                                </Tooltip>
                                <Tooltip text="Cancel Follow-up">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    icon={<HiOutlineXCircle className="w-4 h-4 text-red-400" />}
                                    onClick={() => handleMarkCancelled(fu)}
                                  />
                                </Tooltip>
                              </>
                            )}
                            {fu.leadId?._id && (
                              <Tooltip text="View Lead">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  icon={<HiOutlinePhone className="w-4 h-4 text-gray-400" />}
                                  onClick={() => navigate(`/app/leads/${fu.leadId._id}`)}
                                />
                              </Tooltip>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {hoverTooltip.visible && (
                <div
                  className="fixed px-2.5 py-1.5 text-xs font-medium text-white bg-gray-800 rounded-md whitespace-nowrap z-[99999] pointer-events-none shadow-lg"
                  style={{ top: hoverTooltip.top, left: hoverTooltip.left, transform: 'translate(-50%, -100%)' }}
                >
                  📝 {hoverTooltip.notes}
                  <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-4 border-l-transparent border-r-transparent border-b-gray-800" />
                </div>
              )}
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-gray-100">
              {followUps.map((fu) => {
                const overdue = isOverdue(fu);
                const leadName = fu.leadId?.name || "Unknown";
                const leadPhone = fu.leadId?.phone || "-";
                const assigneeName = fu.assignedTo
                  ? `${fu.assignedTo.firstName} ${fu.assignedTo.lastName}`
                  : "Unassigned";

                return (
                  <div
                    key={fu._id}
                    className={`p-3 sm:p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                      overdue ? "bg-red-50/30" : ""
                    }`}
                    onClick={() => {
                      if (fu.leadId?._id) {
                        navigate(`/app/leads/${fu.leadId._id}`);
                      }
                    }}
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0 flex-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => {
                            if (fu.leadId?._id) {
                              navigate(`/app/leads/${fu.leadId._id}`);
                            }
                          }}
                          className="font-medium text-gray-900 text-sm hover:text-primary-600 truncate block max-w-full"
                          disabled={!fu.leadId?._id}
                        >
                          {leadName}
                        </button>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-gray-500 truncate">
                            {leadPhone}
                          </span>
                          <span className="text-xs text-gray-400">•</span>
                          <Tooltip text={assigneeName}>
                            <span className="text-xs text-gray-500 truncate max-w-[120px] block">
                              {assigneeName}
                            </span>
                          </Tooltip>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {getStatusBadge(fu.status)}
                        {overdue && (
                          <span className="text-xs font-medium text-red-500">
                            Overdue
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Info Row */}
                    <div className="flex items-center justify-between text-xs sm:text-sm">
                      <div className="flex items-center gap-3">
                        <span className={`${overdue ? "text-red-600 font-medium" : "text-gray-700"}`}>
                          {formatDate(fu.scheduledDate)}
                        </span>
                        <span className="text-gray-400">•</span>
                        <span className="text-gray-600">
                          {fu.scheduledTime || "-"}
                        </span>
                      </div>
                    </div>

                    {/* Notes */}
                    {fu.notes && (
                      <Tooltip text={fu.notes}>
                        <p className="text-xs text-gray-400 mt-1 truncate">
                          {fu.notes}
                        </p>
                      </Tooltip>
                    )}

                    {/* Actions */}
                    {fu.status === "pending" && (
                      <div className="flex items-center gap-1 pt-2 mt-2 border-t border-gray-100" onClick={(e) => e.stopPropagation()}>
                        <Tooltip text="Mark Complete">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-2 text-green-600"
                            icon={<HiOutlineCheckCircle className="w-4 h-4" />}
                            onClick={() => handleMarkComplete(fu)}
                          >
                            <span className="text-xs">Complete</span>
                          </Button>
                        </Tooltip>
                        <Tooltip text="Cancel Follow-up">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-2 text-red-400"
                            icon={<HiOutlineXCircle className="w-4 h-4" />}
                            onClick={() => handleMarkCancelled(fu)}
                          >
                            <span className="text-xs">Cancel</span>
                          </Button>
                        </Tooltip>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-3 sm:p-4 border-t border-gray-100">
                <span className="text-xs sm:text-sm text-gray-500">
                  Page {pagination.page} of {pagination.totalPages} ·{" "}
                  {pagination.total} total
                </span>
                <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-center">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    <span className="hidden sm:inline">Previous</span>
                    <span className="sm:hidden">Prev</span>
                  </Button>

                  {buildPageList(pagination.page, pagination.totalPages).map(
                    (item, idx) =>
                      item === "..." ? (
                        <span
                          key={`ellipsis-${idx}`}
                          className="px-2 text-sm text-gray-400 select-none"
                        >
                          …
                        </span>
                      ) : (
                        <button
                          key={item}
                          type="button"
                          onClick={() => setPage(item)}
                          className={
                            page === item
                              ? "min-w-[34px] px-2.5 py-1.5 rounded-lg text-sm font-semibold bg-primary-600 text-white transition-colors"
                              : "min-w-[34px] px-2.5 py-1.5 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                          }
                        >
                          {item}
                        </button>
                      )
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === pagination.totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
          </Card>
        )}
        </>
        ) : (
          <FollowUpsCalendar />
        )}

        {/* Mark Complete Modal */}
        <Modal
          isOpen={showCompleteModal}
          onClose={() => {
            setShowCompleteModal(false);
            setSelectedFollowUp(null);
          }}
          title="Mark Follow-up Complete"
          size="sm"
        >
          <div className="space-y-3 sm:space-y-4">
            {selectedFollowUp && (
              <>
                <p className="text-gray-600 text-sm">
                  Mark the follow-up with{" "}
                  <span className="font-semibold">
                    {selectedFollowUp.leadId?.name || "Unknown"}
                  </span>{" "}
                  on{" "}
                  <span className="font-semibold">
                    {selectedFollowUp.scheduledDate
                      ? format(
                          parseISO(selectedFollowUp.scheduledDate),
                          "MMM d, yyyy"
                        )
                      : "N/A"}
                  </span>{" "}
                  as completed?
                </p>
                {selectedFollowUp.notes && (
                  <div className="bg-gray-50 rounded-lg p-2.5 sm:p-3">
                    <p className="text-xs text-gray-500">Notes</p>
                    <p className="text-sm text-gray-700">
                      {selectedFollowUp.notes}
                    </p>
                  </div>
                )}
              </>
            )}
            <div className="flex gap-2 sm:gap-3 justify-end pt-3 sm:pt-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowCompleteModal(false);
                  setSelectedFollowUp(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={confirmMarkComplete}
                loading={updateMutation.isPending}
              >
                Mark Complete
              </Button>
            </div>
          </div>
        </Modal>

        {/* Cancel Follow-up Confirm Dialog */}
        <ConfirmDialog
          isOpen={showCancelConfirm}
          onClose={() => { setShowCancelConfirm(false); setFollowUpToCancel(null); }}
          onConfirm={confirmCancelFollowUp}
          title="Cancel Follow-up"
          message={followUpToCancel ? `Are you sure you want to cancel the follow-up with ${followUpToCancel.leadId?.name || 'Unknown'} on ${followUpToCancel.scheduledDate ? format(parseISO(followUpToCancel.scheduledDate), 'MMM d, yyyy') : 'N/A'}? This action cannot be undone.` : 'Are you sure you want to cancel this follow-up?'}
          confirmText="Yes, Cancel"
          cancelText="Keep It"
          variant="warning"
          icon={HiOutlineXCircle}
          loading={updateMutation.isPending}
        />
      </div>
    </Layout>
  );
};

export default FollowUpsList;